import { useState, useEffect } from 'react';
import { Player } from '@lottiefiles/react-lottie-player';
import { TFunction, useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { SingleLineText, TitlePart, AutoComplete, Button as RawButton, LoaderPending } from '@bloomays-lib/ui.shared';
import CardTalent from './CardTalent';
import { cancel, notify } from '../../helpers/toastify';
import { styled } from '@mui/material/styles';
import {
  ApolloGqlJobFunnel,
  ITalent,
  StatusType,
  SyncCandidatJobInput,
  JobFunnelPhase,
} from '@bloomays-lib/types.shared';
import { getApolloErrorCode } from '../../helpers/error';
import { ADD_TALENT_TO_JOB_FUNNEL, GET_JOB_FUNNEL_PROJECTS } from '@bloomays-lib/adapter.api-talent';
import { getATSJobUrl } from '@bloomays-lib/utils.shared';
import CardJob from './CardJob';
import { useUserStore } from '../../stores/userStore';

export type SyncTalentQualificationProps = {
  talent: ITalent;
  handleClose: () => void;
};

const toastId = 'toastSyncTalentNotification';
const toastOptions = { toastId, hideProgressBar: true };

const handleUpsertCandiError = (t: TFunction<'notify', undefined>, jobId: string) => (e: any) => {
  const errorCode = getApolloErrorCode(e);
  switch (errorCode) {
    case 'ATS_UNEXPECTED_ERROR': {
      notify(
        'error',
        "Nous avons pas pu positionner le Talent sur l'ATS, il faut le positionner manuellement. Voici le message d'erreur :" +
          e.message +
          '. Contact les tech.',
        e,
        toastOptions,
        getATSJobUrl(import.meta.env.VITE_ENV, jobId),
      );
      return;
    }
    default:
      notify('error', t('randomUpdateError', { ns: 'notify' }), e, toastOptions);
  }
};

const updateAutocompleteOptions = (jobFunnel: ApolloGqlJobFunnel[]): ApolloGqlJobFunnel[] => {
  return jobFunnel.map((jobFunnel) => ({
    ...jobFunnel,
    name: `${jobFunnel.name} chez ${jobFunnel.society.name}`,
  }));
};

const SyncTalentQualification = ({ talent, handleClose }: SyncTalentQualificationProps) => {
  const { data: dataJobs, refetch: refetchJobs } = useQuery<{
    getJobFunnelProjects: ApolloGqlJobFunnel[];
  }>(GET_JOB_FUNNEL_PROJECTS, {
    variables: {
      status: StatusType.inProgress,
    },
    context: { clientName: 'api.talents' },
  });
  const [addTalentToJobFunnel, { loading: addTalentToJobFunnelLoading }] = useMutation<
    { addTalentToJobFunnel: { hiresweetId: string } },
    SyncCandidatJobInput
  >(ADD_TALENT_TO_JOB_FUNNEL, { context: { clientName: 'api.talents' } });
  const { t } = useTranslation(['synchronizationTalent', 'notify']);
  const [animationData, setAnimationData] = useState<any>();
  const [animationError, setAnimationError] = useState<boolean>(false);
  const [jobValue, setJobValue] = useState<ApolloGqlJobFunnel>();
  const user = useUserStore((state) => state.user);

  useEffect(() => {
    const loadAnimation = async () => {
      try {
        const animation = await import('../../graphic-charter/animations/arrowToRight.json');
        const animationData = animation.default || animation;
        setAnimationData(animationData);
        setAnimationError(false);
      } catch (error) {
        console.error("Erreur lors du chargement de l'animation:", error);
        setAnimationError(true);
      }
    };

    loadAnimation();
  }, []);

  // reload after first open
  useEffect(() => {
    if (dataJobs) {
      refetchJobs();
    }
  }, [refetchJobs, dataJobs]);

  const upsertCandiQualif = async () => {
    if (!jobValue?.id) {
      notify('error', t('missingCandidateInfos', { ns: 'notify' }), null, toastOptions);
      return;
    }
    if (!user?.profile.email) {
      notify('error', "Email de l'utilisateur non trouvé", null, toastOptions);
      return;
    }
    try {
      const result = await addTalentToJobFunnel({
        variables: {
          talentId: talent.id as string,
          jobId: jobValue.id,
          author: user.profile.email,
        },
      });

      handleClose();
      cancel();
      const atsURL = jobValue?.hubspotDealId
        ? getATSJobUrl(
            import.meta.env.VITE_ENV,
            jobValue?.hubspotDealId,
            result.data?.addTalentToJobFunnel?.hiresweetId || talent.hiresweetId,
          )
        : undefined;
      notify('success', 'Talent ajouté au JobFunnel', null, toastOptions, atsURL);
    } catch (e: any) {
      cancel();
      handleUpsertCandiError(t, jobValue.id)(e);
    }
  };

  const jobs = updateAutocompleteOptions(dataJobs?.getJobFunnelProjects || []);

  const renderAnimation = () => {
    if (animationError) {
      return <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Error</div>;
    }

    if (!animationData) {
      return <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>...</div>;
    }

    return (
      <Player
        src={animationData}
        autoplay={true}
        loop={true}
        style={{ height: 100 }}
        onEvent={(event) => {
          if (event === 'error') setAnimationError(true);
        }}
      />
    );
  };

  return (
    <div>
      <div>
        {addTalentToJobFunnelLoading && <LoaderPending text={t('syncCandiFunnelInProgress', { ns: 'notify' })} />}
        <TitlePart textTitle={t('title')} />
        <SingleLineText text={t('subtitle')} />
        <ContainerJobChoices>
          <AutoComplete data={jobs} label={t('jobChoice')} setValue={setJobValue} />
          {/* synchro_talent used by marketing*/}
          <Button
            id="synchro_talent"
            textButton={t('validate')}
            disable={!jobValue?.id || addTalentToJobFunnelLoading}
            onClick={() => upsertCandiQualif()}
          />
        </ContainerJobChoices>
        <ContainerCards>
          <CardTalent
            talent={talent}
            talentProcess={{
              phase: JobFunnelPhase.Prequalification,
              salesPriceWithoutTaxes: talent.tjm || talent.package,
            }}
          />
          {renderAnimation()}
          {jobValue && <CardJob job={jobValue} />}
        </ContainerCards>
      </div>
    </div>
  ) as React.ReactNode;
};

export default SyncTalentQualification;
export { SyncTalentQualification };

const ContainerCards = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
}));

const ContainerJobChoices = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const Button = styled(RawButton)(({ theme }) => ({
  marginLeft: '10px',
  border: 'none',
  padding: '2em 1.4em',
  borderRadius: '8px',
  backgroundColor: theme.palette.red[60],
  color: theme.palette.neutral.white,
  boxShadow: '2px 2px 5px rgb(201, 201, 201)',
  margin: '0.8em auto 0.8em 1em',
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: theme.palette.neutral.white,
    color: theme.palette.red[60],
    transition: 'ease 0.3s',
  },
  '&:disabled': {
    border: `1px solid ${theme.palette.neutral[50]}`,
    backgroundColor: theme.palette.neutral[10],
    color: theme.palette.neutral[20],
    '&:hover': {
      cursor: 'not-allowed',
    },
  },
}));
